import { BudgetConfigDefaultDto, BudgetConfigDto, BudgetConfigValueType, BudgetDto, BudgetGeneralityDto, BudgetLineDto, BudgetToPdfDto } from 'api/budgets/models/BudgetDto';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DECIMAL_PLACES, LOGGER_LOG_TYPE, BODY_TOOLTIP, DATE_TIME_FORMAT_DEFAULT } from 'Config';
import BudgetsService from 'api/budgets/BudgetsService';
import { convertParameterExpressions, loadCatalogParametersForBudget, waitForCatalogParameters, LinesTableColumn, mergeWithExistingParameters } from './lines/utils';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Loading from 'common/services/Loading';
import { FormProvider, useForm } from 'react-hook-form';
import { Tab, Tabs } from 'react-bootstrap';
import Button from 'common/components/button/Button';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import { FaEye, FaFileExcel, FaHistory, FaPencilAlt, FaPlus, FaSyncAlt, FaTrashAlt, FaUpload } from 'react-icons/fa';
import { Reducers } from 'store/types';
import { useSelector, useDispatch } from 'react-redux';
import UsersService from 'api/users/UsersService';
import { UserProfile } from 'api/account/models/UserProfile';
import Toast from 'common/services/Toast';
import { SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import BudgetDetails from './details/BudgetDetails';
import BudgetLines from './lines/BudgetLines';
import BudgetWorkTab from './work/BudgetWorkTab';
import WorksService from 'api/works/WorksService';
import ExternalPartsService from 'api/externalPart/ExternalPartsService';
import { BudgetStatus } from 'api/budgets/enums/BudgetStatus';
import { BudgetType } from 'api/budgets/enums/BudgetType';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import UnitsService from 'api/units/UnitsService';
import TaxesService from 'api/taxes/TaxesService';
import { markDeletedTaxes } from 'api/taxes/taxCatalogUtils';
import { UnitDto } from 'api/units/models/UnitDto';
import { TaxDto } from 'api/taxes/models/TaxDto';
import styles from './BudgetScreen.module.scss';
import ColumnsOptionsSidebar, { ColumnsOptionsSidebarColumn, ColumnsOptionsSidebarColumnrder, columnsReorder } from 'common/components/columnsOptionsSidebar/ColumnsOptionsSidebar';
import { Form } from 'common/components/form/Form';
import { normalizeDateOnly } from 'common/utils/normalizeDateOnly';
import BudgetsCache, { BudgetsCacheData } from './utils/BudgetsCache';
import { calculateAllLinesTotals, calculateBudgetTotals, flushPendingBudgetCalculations, hydrateClipboardFromStorage, setBudget, setLines, setOpenLinesIndexes, setSelectedLineKey, updateBudget, updateLine } from 'store/budget/action';
import { createLine } from 'store/budget/reducer';
import store from 'store/store';
import WorkTypesService from 'api/workTypes/WorkTypesService';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';
import BudgetPrices from './prices/BudgetPrices';
import BudgetGeneralities, { buildBudgetGeneralityFromDefault, pickGeneralityDesignation, GENERALITY_MAX_TEXT_LENGTH } from './generalities/BudgetGeneralities';
import BudgetGeneralityDefaultsService from 'api/budgetGeneralityDefaults/BudgetGeneralityDefaultsService';
import { BudgetGeneralityDefaultDto } from 'api/budgetGeneralityDefaults/models/BudgetGeneralityDefaultDto';
import BudgetSummary, { buildPredefinedConfigEntry, getConfigFieldNames, getElementTypesFromLines, getGroupedParamsFromLines, getSummaryColumnFieldNames, stripGeneralityReferences } from './summary/BudgetSummary';
import Storage from 'common/services/Storage';
import BudgetFileModelsService from 'api/budgetFileModels/BudgetFileModelsService';
import LanguagesService from 'api/languages/LanguagesService';
import ButtonDropdown, { ButtonDropdownOption } from 'common/components/buttonDropdown/ButtonDropdown';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';
import HistoryModal from 'common/components/modal/historyModal/HistoryModal';
import { Header, Text } from 'common/components/texts/Texts';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { BudgetHistoriesDto } from 'api/budgets/models/BudgetHistoriesDto';
import { BudgetLineParameterByElementDto } from 'api/budgets/models/BudgetLineParameterDto';
import InfoMessage from 'common/components/infoMessage/InfoMessage';
import BudgetsFieldsService from 'api/budgetsFields/BudgetsFieldsService';
import { formatBudgetVersion, mapFieldValues } from './utils/BudgetUtils';
import SuppliersService from 'api/suppliers/SuppliersService';
import { SupplierDto } from 'api/suppliers/models/SupplierDto';
import ElementsService from 'api/elements/ElementsService';
import { ElementTypeDto } from 'api/elements/Models/ElementTypeDto';
import ImportStructureModal from './lines/importStructure/ImportStructureModal';
import ExportMaterialsListModal from './exportMaterials/ExportMaterialsListModal';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import InformationModal from 'common/components/modal/informationModal/InformationModal';

const PAGE_ID = 'BUDGET_LINES';
const url = '/business/budgets';

type TabKey = 'details' | 'work' | 'lines' | 'generalities' | 'prices' | 'summary';
const SUMMARY_HIDDEN_COLUMNS_KEY = 'BUDGET_SUMMARY_HIDDEN_COLUMNS';
const SUMMARY_COLUMNS_ORDER_KEY = 'BUDGET_SUMMARY_COLUMNS_ORDER';

type FormModel = Omit<BudgetDto, 'lines'>;

// Prefixo gravado no motivo do histórico quando a revisão foi criada com uma mudança de estado.
// O novo estado vem a seguir ao prefixo (ex.: "STATUS_CHANGE:PENDING_BUDGET"). A entrada congelada
// guarda o estado ANTIGO no seu campo status, por isso mostra-se "de [status] para [novo]".
const STATUS_CHANGE_PREFIX = 'STATUS_CHANGE:';

// Prefixo gravado no motivo quando a versão foi arquivada por se ter copiado conteúdo de outra versão.
// A seguir ao prefixo vem a origem como "número:revisão:versão" (ex.: "COPIED_FROM:LCM-1.A.26:1:1.2").
const COPIED_FROM_PREFIX = 'COPIED_FROM:';

// Letra da revisão a partir da versão (base-26 bijetiva: 1 -> A, 26 -> Z, 27 -> AA),
// igual ao ToAlpha do NumerationService no backend, para coincidir com a letra da referência.
const versionToLetter = (version: number): string => {
    let n = Math.trunc(version);
    if (n <= 0) { return String(version); }
    let s = '';
    while (n > 0) {
        n--;
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26);
    }
    return s;
};

function BudgetScreen(): JSX.Element | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, type, version } = useParams<{ id: string, type: 'create' | 'details' | 'edit' | 'version', version: string }>();
    const [searchParams] = useSearchParams();
    const originBudgetId = searchParams.get('originBudgetId') ?? undefined;
    const duplicateFromId = searchParams.get('duplicateFromId') ?? undefined;
    const [originBudgets, setOriginBudgets] = useState<SelectInputOption[]>([]);
    const form = useForm<FormModel>({ shouldUnregister: false });
    const [loaded, setLoaded] = useState<boolean>(false);
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');
    const isDetails = type === 'details' || type === 'version';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showCannotDeleteWonModal, setShowCannotDeleteWonModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showUpdatePricesModal, setShowUpdatePricesModal] = useState(false);
    const [showSaveRevisionModal, setShowSaveRevisionModal] = useState(false);
    const [showExportMaterialsModal, setShowExportMaterialsModal] = useState(false);
    const [restoreTarget, setRestoreTarget] = useState<BudgetHistoriesDto | null>(null);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = type !== 'version' && UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_WRITE'])
    // Deleting a budget is governed by its own permission, independent of write access.
    const canDelete = type !== 'version' && UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_DELETE'])
    // Starting a revision from a history version is allowed even while viewing a version (read-only),
    // so it depends only on the write policy, not on the current mode.
    const canStartRevision = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_READ'])
    const [externalParts, setExternalParts] = useState<SearchableSelectTitleSubTitleOption[]>([]);
    const [works, setWorks] = useState<SearchableSelectTitleSubTitleOption[]>([]);
    const [workContacts, setWorkContacts] = useState<SearchableSelectTitleSubTitleOption[]>([])
    const [workTypes, setWorkTypes] = useState<SelectInputOption[]>([]);
    const [currentTabKey, setCurrentTabKey] = useState<TabKey>('details');
    const [taxes, setTaxes] = useState<TaxDto[]>([]);
    const [units, setUnits] = useState<UnitDto[]>([]);
    const [suppliers, setSuppliers] = useState<SupplierDto[]>([]);
    const [elementTypes, setElementTypes] = useState<ElementTypeDto[]>([]);
    const [budgetFileModels, setBudgetFileModels] = useState<SelectInputOption[]>([]);
    const [languages, setLanguages] = useState<SelectInputOption[]>([]);
    const [languageIsoById, setLanguageIsoById] = useState<Record<string, string>>({});
    const [generalityDefaults, setGeneralityDefaults] = useState<BudgetGeneralityDefaultDto[]>([]);
    const [budgetData, setBudgetData] = useState<BudgetsCacheData>(BudgetsCache.get(PAGE_ID, {
        hiddenColumns: [
            LinesTableColumn.GROSS_UNIT_PRICE,
            LinesTableColumn.MARGIN,
            LinesTableColumn.DISCOUNT,
            LinesTableColumn.DECENNIAL_INSURANCE,
            LinesTableColumn.AGGREGATED_ELEMENTS,
            LinesTableColumn.TOTAL_COST_UNIT_PRICE,
            LinesTableColumn.NET_PROFIT,
            LinesTableColumn.NET_PROFIT_PERCENTAGE,
            LinesTableColumn.TOTAL_INCLUDING_TAX,
        ],
    }));
    const [budgetHistories, setBudgetHistories] = useState<BudgetHistoriesDto[]>([]);

    const [printBudgetWithTechnicalFile,setPrintBudgetWithTechnicalFile] = useState<boolean | null>(null)
    const budget = useSelector<Reducers, Partial<BudgetDto>>(x => x.budget.budget);
    const budgetLines = useSelector<Reducers, BudgetLineDto[]>(state => state.budget.lines);
    const [showInvalidReferenceModal, setShowInvalidReferenceModal] = useState(false);
    const [summaryHiddenColumns, setSummaryHiddenColumns] = useState<string[]>(() => {
        return Storage.getObject(SUMMARY_HIDDEN_COLUMNS_KEY) ?? [];
    });
    const [summaryColumnsOrder, setSummaryColumnsOrder] = useState<ColumnsOptionsSidebarColumnrder<string>[]>(() => {
        return Storage.getObject(SUMMARY_COLUMNS_ORDER_KEY) ?? [];
    });
    const [configDefaults, setConfigDefaults] = useState<BudgetConfigDefaultDto[]>([]);

    const dispatch = useDispatch();

    const linesTableColumns: ColumnsOptionsSidebarColumn<LinesTableColumn>[] = [
        {
            name: t('budgets.budget.lines.header.element_image'),
            field: LinesTableColumn.ELEMENT_IMAGE,
        },
        {
            name: t('budgets.budget.lines.header.line_number'),
            field: LinesTableColumn.LINE_NUMBER,
        },
        {
            name: t('budgets.budget.lines.header.quantity'),
            field: LinesTableColumn.QUANTITY,
        },
        {
            name: t('budgets.budget.lines.header.unity'),
            field: LinesTableColumn.UNIT,
        },
        {
            name: t('budgets.budget.lines.header.gross_unit_price'),
            field: LinesTableColumn.GROSS_UNIT_PRICE,
        },
        {
            name: t('budgets.budget.lines.header.discount'),
            field: LinesTableColumn.DISCOUNT,
        },
        {
            name: t('budgets.budget.lines.header.aggregated_elements'),
            field: LinesTableColumn.AGGREGATED_ELEMENTS,
        },
        {
            name: t('budgets.budget.lines.header.total_cost_unit_price'),
            field: LinesTableColumn.TOTAL_COST_UNIT_PRICE,
        },
        {
            name: t('budgets.budget.lines.header.margin'),
            field: LinesTableColumn.MARGIN,
        },
        {
            name: t('budgets.budget.lines.header.decennial_insurance'),
            field: LinesTableColumn.DECENNIAL_INSURANCE,
        },
        {
            name: t('budgets.budget.lines.header.price_per_unity_tooltip'),
            field: LinesTableColumn.UNIT_PRICE,
        },
        {
            name: t('budgets.budget.lines.header.total_without_tax'),
            field: LinesTableColumn.TOTAL_EXCLUDING_TAX,
        },
        {
            name: t('budgets.budget.lines.header.tax'),
            field: LinesTableColumn.TAX,
        },
        {
            name: t('budgets.budget.lines.header.total_with_tax'),
            field: LinesTableColumn.TOTAL_INCLUDING_TAX,
        },
        {
            name: t('budgets.budget.lines.header.net_profit'),
            field: LinesTableColumn.NET_PROFIT,
        },
        {
            name: t('budgets.budget.lines.header.net_profit_percentage'),
            field: LinesTableColumn.NET_PROFIT_PERCENTAGE,
        },
    ];

    const summaryTableColumns: ColumnsOptionsSidebarColumn<string>[] = useMemo(() => {
        const elementTypes = getElementTypesFromLines(budgetLines);
        const groupedParams = getGroupedParamsFromLines(budgetLines);
        let cols = getSummaryColumnFieldNames(elementTypes, groupedParams, t).map(col => ({
            field: col.field,
            name: col.name,
        }));

        for (const co of summaryColumnsOrder) {
            const sourceIndex = cols.findIndex(x => x.field === co.field);
            if (sourceIndex > -1) {
                cols = columnsReorder([...cols], sourceIndex, co.i);
            }
        }

        return cols;
    }, [budgetLines, t, summaryColumnsOrder]);

    const summaryConfigColumns: ColumnsOptionsSidebarColumn<string>[] = useMemo(() => {
        return getConfigFieldNames(t, budget.configs ?? []).map(col => ({
            field: col.field,
            name: col.name,
        }));
    }, [t, budget.configs]);

    const summaryConfigHidden: string[] = useMemo(() => {
        const activeKeys = new Set((budget.configs ?? []).map(c => c.key));
        return summaryConfigColumns.map(c => c.field).filter(f => !activeKeys.has(f));
    }, [summaryConfigColumns, budget.configs]);

    const onChangeSummaryHiddenColumns = (hc: string[]) => {
        setSummaryHiddenColumns(hc);
        Storage.setObject(SUMMARY_HIDDEN_COLUMNS_KEY, hc);
    };

    const onChangeSummaryColumnsOrder = (oc: ColumnsOptionsSidebarColumnrder<string>[]) => {
        setSummaryColumnsOrder(oc);
        Storage.setObject(SUMMARY_COLUMNS_ORDER_KEY, oc);
    };

    const onChangeSummaryConfigHidden = (newHidden: string[]) => {
        const currentConfigs = budget.configs ?? [];
        const currentActiveKeys = currentConfigs.map(c => c.key);
        const currentActive = new Set(currentActiveKeys);
        const newHiddenSet = new Set(newHidden);

        const toRemove = currentActiveKeys.filter(k => newHiddenSet.has(k));
        const toAdd = summaryConfigColumns.map(c => c.field).filter(k => !currentActive.has(k) && !newHiddenSet.has(k));

        if (toRemove.length === 0 && toAdd.length === 0) return;

        const elementTypesFromLines = getElementTypesFromLines(budgetLines);
        let updated = currentConfigs.filter(c => !toRemove.includes(c.key));
        for (const key of toAdd) {
            updated = [...updated, buildPredefinedConfigEntry(key, configDefaults, elementTypesFromLines)];
        }
        dispatch(updateBudget({ configs: updated }));
    };

    const onChangeSummaryConfigOrder = (oc: ColumnsOptionsSidebarColumnrder<string>[]) => {
        const currentConfigs = budget.configs ?? [];
        if (currentConfigs.length === 0) return;
        const working = [...currentConfigs];
        for (const co of oc) {
            const sourceIndex = working.findIndex(c => c.key === co.field);
            if (sourceIndex > -1) {
                const target = Math.min(co.i, working.length - 1);
                const [removed] = working.splice(sourceIndex, 1);
                working.splice(target, 0, removed);
            }
        }
        dispatch(updateBudget({ configs: working }));
    };

    // Fields a TS (trabalho suplementar) inherits from its originating budget. Used both when
    // the TS is created from the "Adicionar trabalho suplementar" button (originBudgetId in the
    // URL — see getData) and when the origin budget is picked manually (see onChangeOriginBudget).
    // The *Name/*SubTitle fields are carried so the dropdowns can show the inherited option even
    // before their catalogs are (re)loaded.
    const getInheritableBudgetFields = (origin: BudgetDto): Partial<BudgetDto> => ({
        workTypeId: origin.workTypeId,
        workTypeName: origin.workTypeName,
        workId: origin.workId,
        workName: origin.workName,
        workSubTitle: origin.workSubTitle,
        externalPartId: origin.externalPartId,
        languageId: origin.languageId,
        onSiteContactId: origin.onSiteContactId,
        onSiteContactName: origin.onSiteContactName,
        onSiteContactSubTitle: origin.onSiteContactSubTitle,
        managerContactId: origin.managerContactId,
        managerContactName: origin.managerContactName,
        managerContactSubTitle: origin.managerContactSubTitle,
        defaultTaxId: origin.defaultTaxId,
        defaultTaxValue: origin.defaultTaxValue,
        defaultDiscountPercentage: origin.defaultDiscountPercentage,
        defaultMarginPercentage: origin.defaultMarginPercentage,
        defaultCommissionMarginPercentage: origin.defaultCommissionMarginPercentage,
        defaultDecennialInsurancePercentage: origin.defaultDecennialInsurancePercentage,
        budgetFileModelId: origin.budgetFileModelId,
        decimalPlaces: origin.decimalPlaces,
        paymentTermsTranslations: origin.paymentTermsTranslations,
    });

    const generateReference = () => {
        return BudgetsService.generateReference({
            workTypeId: form.getValues('workTypeId'),
            type: form.getValues('type'),
            originBudgetId: form.getValues('originBudgetId'),
            date: form.getValues('date'),
        }).then((data) => {
            form.setValue('number', data.reference);
        });
    }

    // Materializa uma generalidade por defeito do backoffice, usada na importação manual pelo botão
    // "Adicionar generalidade" na tab Generalidades. As expressões são avaliadas com os totais
    // "lines-only" atuais (do Redux, mantidos pelo motor de cálculo das linhas) para não as tornar
    // auto-referenciais, e a designação é escolhida na língua do orçamento.
    const materializeGeneralityDefault = useCallback((d: BudgetGeneralityDefaultDto): BudgetGeneralityDto => {
        const b: any = store.getState().budget.budget;
        const linesNet = b.linesTotalNetExcludingTax ?? b.totalNetExcludingTax ?? 0;
        const linesExcl = b.linesTotalExcludingTax ?? b.totalExcludingTax ?? 0;
        const linesTax = b.linesTotalTax ?? b.totalTax ?? 0;
        const linesIncl = b.linesTotalIncludingTax ?? b.totalIncludingTax ?? 0;

        const defaultTaxId = form.getValues('defaultTaxId') ?? null;
        const defaultTaxValue = taxes.find(tx => tx.id === defaultTaxId)?.value ?? form.getValues('defaultTaxValue') ?? 0;
        const marginPct = form.getValues('defaultMarginPercentage') ?? 0;
        const commissionPct = form.getValues('defaultCommissionMarginPercentage') ?? 0;

        const budgetExpressionParams = [
            { id: ExpressionSystem.formatParameterId(['budget', 'totalNetExcludingTax']), name: '', value: linesNet },
            { id: ExpressionSystem.formatParameterId(['budget', 'linesTotalNetExcludingTax']), name: '', value: linesNet },
            { id: ExpressionSystem.formatParameterId(['budget', 'totalExcludingTax']), name: '', value: linesExcl },
            { id: ExpressionSystem.formatParameterId(['budget', 'totalTax']), name: '', value: linesTax },
            { id: ExpressionSystem.formatParameterId(['budget', 'totalIncludingTax']), name: '', value: linesIncl },
            { id: ExpressionSystem.formatParameterId(['budget', 'defaultTaxValue']), name: '', value: defaultTaxValue },
            { id: ExpressionSystem.formatParameterId(['budget', 'defaultMarginPercentage']), name: '', value: marginPct },
            { id: ExpressionSystem.formatParameterId(['budget', 'defaultCommissionMarginPercentage']), name: '', value: commissionPct },
            { id: ExpressionSystem.formatParameterId(['budget', 'defaultCombinedMarginPercentage']), name: '', value: marginPct + commissionPct },
        ];

        const budgetLanguageIso = languages.find(l => l.value === form.getValues('languageId'))?.label
            ?? languages[0]?.label
            ?? '';
        const pickDesignation = (translations: { languageIso: string; designation: string }[] | undefined) =>
            pickGeneralityDesignation(translations, budgetLanguageIso);

        return buildBudgetGeneralityFromDefault(d, budgetExpressionParams, defaultTaxId, defaultTaxValue, pickDesignation);
    }, [form, taxes, languages]);

    const getData = async () => {
        loadCatalogParametersForBudget();
        try {
            let result: Partial<BudgetDto> = {
                id,
                description: '',
                lines: [],
                status: BudgetStatus.IN_STUDY,
                type: BudgetType.BUDGET,
                totalExcludingTax: 0,
                totalIncludingTax: 0,
                totalNetExcludingTax: 0,
                totalTax: 0,
                budgetHistoryVersion: '1',
                decimalPlaces: DEFAULT_DECIMAL_PLACES,
                fieldsValues: [],
                generalities: [],
            }

            // Creating a TS from an originating budget: inherit every inheritable field (work,
            // family, third party, contacts, language, defaults…) and force the TS type. The
            // rest of getData then loads the dependent catalogs (works, contacts) for these ids.
            if (!id && originBudgetId) {
                const origin = await BudgetsService.getById(originBudgetId, true);
                result.type = BudgetType.TS;
                result.originBudgetId = originBudgetId;
                Object.assign(result, getInheritableBudgetFields(origin));
            }

            // Duplicar: copiar o orçamento COMPLETO (linhas, generalidades, fieldsValues, configs
            // da tab Resumo, expressões, defaults, contactos, tipo…) e limpar apenas a identidade.
            // O número é gerado no backend; aqui fica vazio (preview cosmético no create).
            // Mantém-se o type/originBudgetId originais (duplicar um TS continua TS).
            if (!id && duplicateFromId) {
                const origin = await BudgetsService.getById(duplicateFromId, false);
                result = {
                    ...origin,
                    id: undefined,
                    number: '',
                    sequentialNumber: undefined,
                    version: undefined,
                    budgetHistoryVersion: '1',
                    isPrinted: false,
                    updatedDate: undefined,
                    userUpdatedName: undefined,
                    status: BudgetStatus.IN_STUDY,
                    date: undefined,
                    validationDate: undefined,
                    wonDate: undefined,
                };
            }

            Loading.show();

            if (id) {
                result = type === 'version' && version
                    ? await BudgetsService.getBudgetHistoryByVersion(id, version)
                    : await BudgetsService.getById(id, isDetails);
            }

            // The origin-budget dropdown (for TS) — number + description of the referenced quote.
            // Fetched always so switching the type to TS in the form still has options.
            setOriginBudgets(await BudgetsService.getCatalog());

            // Taxes already applied to this budget may have been deactivated. Collect their ids so the
            // catalog keeps returning them (visible in edit/details), while creates still see active only.
            const referencedTaxIds = id
                ? Array.from(new Set([
                    result.defaultTaxId,
                    result.discountTaxId,
                    ...(result.lines ?? []).map(l => l.taxId),
                    ...(result.generalities ?? []).map(g => g.taxId),
                ].filter((x): x is string => !!x)))
                : undefined;

            const [externalPartsData, unitsData, taxesData, workTypesData, budgetFileModelsData, budgetsFields, suppliersData, elementTypesData, languagesData, configDefaultsData] = await Promise.all([
                ExternalPartsService.getCatalog(),
                UnitsService.getFormattedCatalog(),
                TaxesService.getFormattedCatalog(referencedTaxIds),
                WorkTypesService.getCatalog(),
                BudgetFileModelsService.getCatalog(),
                BudgetsFieldsService.getCatalog(type === 'version' ? null : id ?? null, type === 'version' ? id ?? null : null),
                SuppliersService.getCatalog(),
                ElementsService.getElementTypesCatalog(),
                LanguagesService.GetAllForSelectItem(),
                BudgetsService.getConfigDefaults().catch(() => []),
            ]);

            setConfigDefaults(configDefaultsData ?? []);

            // Num duplicado os configs (tab Resumo) vêm copiados do original — não os
            // substituir pelos defaults globais.
            if (!id && !duplicateFromId) {
                result.configs = (configDefaultsData ?? []).map(d => ({
                    key: d.key,
                    expression: d.expression,
                    label: d.label,
                    valueType: d.valueType ?? BudgetConfigValueType.NUMBER,
                }));
            }

            try {
                const generalityDefaults = await BudgetGeneralityDefaultsService.getCatalog();
                setGeneralityDefaults(generalityDefaults ?? []);
                const budgetLanguageIso = languagesData?.find(l => l.value === result.languageId)?.label
                    ?? languagesData?.[0]?.label
                    ?? '';

                const pickDesignation = (translations: { languageIso: string; designation: string }[] | undefined) =>
                    pickGeneralityDesignation(translations, budgetLanguageIso);

                const existingGeneralities = result.generalities ?? [];
                const alreadyPresentDefaultIds = new Set(
                    existingGeneralities
                        .map(g => g.sourceGeneralityDefaultId)
                        .filter((x): x is string => !!x)
                );
                const dismissedDefaultIds = new Set(result.dismissedGeneralityDefaultIds ?? []);

                const defaultTax = taxesData.find(tx => tx.id === result.defaultTaxId);
                const defaultTaxId = result.defaultTaxId ?? null;
                const defaultTaxValue = defaultTax?.value ?? 0;

                // Totais "lines-only" (sem generalidades) para avaliar as expressões das
                // generalidades por defeito. O total persistido passou a incluir generalidades,
                // por isso recalculamos a partir das linhas para não tornar as expressões
                // auto-referenciais.
                const rootLineTotals = (result.lines ?? []).filter(l => !l.parentKey);
                const linesNetForExpr = rootLineTotals.reduce((s, l) => s + (l.totalExcludingTax ?? 0), 0);
                const linesTaxForExpr = rootLineTotals.reduce((s, l) => s + (l.totalTax ?? 0), 0);
                const discountTaxValueForExpr = (result.discount ?? 0) * (result.discountTaxValue ?? 0) / 100;
                const linesExclForExpr = linesNetForExpr - (result.discount ?? 0);
                const linesTaxNetForExpr = linesTaxForExpr - discountTaxValueForExpr;
                const linesInclForExpr = linesExclForExpr + linesTaxNetForExpr;

                const budgetExpressionParams = [
                    { id: ExpressionSystem.formatParameterId(['budget', 'totalNetExcludingTax']), name: '', value: linesNetForExpr },
                    { id: ExpressionSystem.formatParameterId(['budget', 'linesTotalNetExcludingTax']), name: '', value: linesNetForExpr },
                    { id: ExpressionSystem.formatParameterId(['budget', 'totalExcludingTax']), name: '', value: linesExclForExpr },
                    { id: ExpressionSystem.formatParameterId(['budget', 'totalTax']), name: '', value: linesTaxNetForExpr },
                    { id: ExpressionSystem.formatParameterId(['budget', 'totalIncludingTax']), name: '', value: linesInclForExpr },
                    { id: ExpressionSystem.formatParameterId(['budget', 'defaultTaxValue']), name: '', value: defaultTaxValue },
                    { id: ExpressionSystem.formatParameterId(['budget', 'defaultMarginPercentage']), name: '', value: result.defaultMarginPercentage ?? 0 },
                    { id: ExpressionSystem.formatParameterId(['budget', 'defaultCommissionMarginPercentage']), name: '', value: result.defaultCommissionMarginPercentage ?? 0 },
                    { id: ExpressionSystem.formatParameterId(['budget', 'defaultCombinedMarginPercentage']), name: '', value: (result.defaultMarginPercentage ?? 0) + (result.defaultCommissionMarginPercentage ?? 0) },
                ];

                // As generalidades por defeito só são pré-carregadas em orçamentos NOVOS (sem id).
                // Ao reabrir um orçamento existente NÃO injetamos defaults: caso contrário
                // mostraríamos generalidades que nunca foram gravadas (inflacionando o "Total s/ IVA")
                // e que reapareceriam mesmo depois de removidas. Num orçamento existente as
                // generalidades adicionam-se sempre explicitamente pelo botão "Adicionar generalidade".
                // Em modo leitura (detalhes/versão) também não, por igual razão.
                const isNewBudget = !id;
                const injectableDefaults = (!isDetails && isNewBudget) ? (generalityDefaults ?? []) : [];
                const newGeneralities = injectableDefaults
                    .filter(d => !alreadyPresentDefaultIds.has(d.id) && !dismissedDefaultIds.has(d.id))
                    .map(d => buildBudgetGeneralityFromDefault(d, budgetExpressionParams, defaultTaxId, defaultTaxValue, pickDesignation));

                result.generalities = [...existingGeneralities, ...newGeneralities];
            } catch (e) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load generality defaults', e);
                result.generalities = result.generalities ?? [];
            }

            setExternalParts(externalPartsData);
            setUnits(unitsData);
            setTaxes(markDeletedTaxes(taxesData, t('common.deleted')));
            setWorkTypes(workTypesData);
            setBudgetFileModels(budgetFileModelsData);
            setSuppliers(suppliersData);
            setElementTypes(elementTypesData);
            setLanguages((languagesData ?? []).map(x => ({
                label: (x.fullLabel ?? x.label) ?? '',
                value: x.value ?? '',
            })));
            setLanguageIsoById(Object.fromEntries(
                (languagesData ?? [])
                    .filter(x => x.value && x.label)
                    .map(x => [x.value as string, x.label as string])
            ));

            if (!result.lines || result.lines.length === 0) {
                const rootSection = {
                    ...createLine(BudgetLineType.SECTION, null, null),
                    isOpen: true,
                };

                result.lines = [
                    rootSection,
                ];
            }

            if (type === 'create' && budgetFileModelsData.length === 1) {
                result.budgetFileModelId = budgetFileModelsData[0].value;
            }

            if (result.workId && result.workName) {
                await getWorks(result.externalPartId);

                setWorks(prevWorks => {
                    const updatedWorks = [...prevWorks];
                    if (!updatedWorks.find(x => x.value === result.workId)) {
                        updatedWorks.push({
                            label: result.workName || '',
                            value: result.workId || '',
                            subTitle: result.workSubTitle ?? '-',
                        });
                    }
                    return updatedWorks;
                });
            } else if (type === 'create') {
                await getWorks(result.externalPartId);
            }

            if (result.workTypeId && result.workTypeName && !workTypesData.find(x => x.value === result.workTypeId)) {
                setWorkTypes([...workTypesData,
                    {
                        label: result.workTypeName,
                        value: result.workTypeId
                    }]);
            }

            const workContactsBuilder: SearchableSelectTitleSubTitleOption[] = []
            if (result.onSiteContactId && result.onSiteContactName && !workContactsBuilder.find(x => x.value === result.onSiteContactId)) {
                workContactsBuilder.push({
                    value: result.onSiteContactId,
                    label: result.onSiteContactName,
                    subTitle: result.onSiteContactSubTitle ?? '-'
                })
            }

            if (result.managerContactId && result.managerContactName && !workContactsBuilder.find(x => x.value === result.managerContactId)) {
                workContactsBuilder.push({
                    value: result.managerContactId,
                    label: result.managerContactName,
                    subTitle: result.managerContactSubTitle ?? '-'
                })
            }

            setWorkContacts(workContactsBuilder)

            const generalitiesNetForState = (result.generalities ?? []).reduce((s, g) => s + (g.totalExcludingTax ?? 0), 0);
            const generalitiesTaxForState = (result.generalities ?? []).reduce((s, g) => s + (g.totalTax ?? 0), 0);

            dispatch(setBudget({
                defaultTaxId: result.defaultTaxId,
                totalNetExcludingTax: result.totalNetExcludingTax ?? 0,
                totalTax: result.totalTax ?? 0,
                totalIncludingTax: result.totalIncludingTax ?? 0,
                totalExcludingTax: result.totalExcludingTax ?? 0,
                generalitiesTotalExcludingTax: generalitiesNetForState,
                generalitiesTotalTax: generalitiesTaxForState,
                discount: result.discount ?? 0,
                discountTaxId: result.discountTaxId || null,
                discountTaxValue: result.discountTaxValue,
                defaultTaxValue: result.defaultTaxValue,
                defaultDiscountPercentage: result.defaultDiscountPercentage ?? 0,
                defaultMarginPercentage: result.defaultMarginPercentage ?? 0,
                defaultCommissionMarginPercentage: result.defaultCommissionMarginPercentage ?? 0,
                defaultDecennialInsurancePercentage: result.defaultDecennialInsurancePercentage ?? 0,
                decimalPlaces: result.decimalPlaces ?? 0,
                decennialInsuranceExpression: result.decennialInsuranceExpression,
                commissionExpression: result.commissionExpression,
                configs: result.configs ?? [],
            }));
            const previousLines = store.getState().budget.lines;
            const openKeys = new Set<string>(
                previousLines
                    .filter((l: { isOpen?: boolean, lineType: string }) => l.isOpen && l.lineType === BudgetLineType.SECTION)
                    .map((l: { key: string }) => l.key)
            );
            const openIndexes = openKeys.size === 0 ? store.getState().budget.openLinesIndexes : [];

            const newLines = result.lines.map((x, i) => {
                const isElementType = x.lineType === BudgetLineType.ELEMENT || x.lineType === BudgetLineType.COMPONENT_ELEMENT;
                return {
                    ...x,
                    isOpen: x.lineType === BudgetLineType.SECTION
                        ? (openKeys.size > 0 ? openKeys.has(x.key) : openIndexes.includes(i))
                        : true,
                    isCustomElement: isElementType && !x.elementId && !!x.designation,
                }
            })

            dispatch(setOpenLinesIndexes([]))
            dispatch(setLines(newLines));
            await waitForCatalogParameters();
            // In details/version mode the backend returns a pruned parameter tree (removes
            // unchecked products and parameters without values). Recalculating here would
            // produce a different total than the one the user saw when saving, because
            // expressions may reference parameters that got pruned. Trust the stored totals.
            if (!isDetails) {
                dispatch(calculateAllLinesTotals());
            } else if (type !== 'version') {
                // Details (orçamento atual, só leitura): as generalidades vêm do backend, por isso
                // dobrá-las no total não perde nada. Não recalculamos os internos das linhas (a
                // árvore de parâmetros vem podada e mudaria os valores).
                dispatch(calculateBudgetTotals());
            }
            // Versão histórica: o snapshot guarda os totais (que JÁ incluíam as generalidades) mas
            // NÃO guarda as generalidades em si. Recalcular aqui faria cair a parcela das
            // generalidades e baixar o total — por isso confiamos nos totais gravados (já aplicados
            // pelo setBudget acima) e não recalculamos nada.

            if (result.workId) {
                await getWorkContacts(result.workId);
            }

            result.fieldsValues = mapFieldValues(budgetsFields, result.fieldsValues ?? []);

            result.lines = [];
            form.reset(result);

            if (type === 'create') {
                await generateReference();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budgets', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setLoaded(true);
        }
    }

    const saveBudget = async (model: FormModel, forceHistoryVersion = false, historyReason?: string): Promise<boolean> => {
        let success = false;
        try {
            Loading.show();

            // Flush any pending debounced calculations so the saved state reflects the latest edits
            flushPendingBudgetCalculations();

            const tempModel: BudgetDto = {
                ...model,
                ...store.getState().budget.budget,
                lines: store.getState().budget.lines,
            }

            // Datas-calendário: ancorar a meio-dia UTC para o dia não saltar quando
            // o valor é guardado em UTC e formatado noutro fuso (ex.: no PDF, no servidor).
            tempModel.date = normalizeDateOnly(tempModel.date);
            tempModel.validationDate = normalizeDateOnly(tempModel.validationDate);
            tempModel.wonDate = normalizeDateOnly(tempModel.wonDate);

            const tempArray: number[] = []
            tempModel.lines.forEach((element,i) => {
                if (element.isOpen) {
                    tempArray.push(i)
                }
            });
            dispatch(setOpenLinesIndexes(tempArray))

            if (tempModel && tempModel.id) {
                await BudgetsService.update(tempModel, forceHistoryVersion, historyReason)
                // navigateTo('details', tempModel.id);
                await getData();
            } else if (tempModel) {
                const id = await BudgetsService.create(tempModel)
                navigateTo('edit', id);
            }
            // Sync config defaults globally (full upsert + delete missing).
            // As referências a generalidades específicas deste orçamento são substituídas por 0 no
            // template global: não existem noutros orçamentos e dariam NOT_FOUND_ERROR ao reutilizar.
            const budgetConfigs: BudgetConfigDto[] = store.getState().budget.budget.configs ?? [];
            BudgetsService.saveConfigDefaults(budgetConfigs.map((c, index) => ({
                key: c.key,
                expression: stripGeneralityReferences(c.expression ?? ''),
                label: c.label,
                order: index,
                valueType: c.valueType ?? BudgetConfigValueType.NUMBER,
            }))).catch((err) => { console.error('Failed to save config defaults', err); });

            Toast.success(t('messages.record_save_success'));
            success = true;
        } catch (error: any) {
            if (error && error.response && error.response.status === 409 && error.response.data === 'invalid_numeration') {
                void generateReference().then(() => {
                    setShowInvalidReferenceModal(true);
                });
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t update save the budget', error);
                Toast.error(t('messages.record_save_error'));
            }
        }
        Loading.hide();
        return success;
    }

    const onSubmit = async (model: FormModel) => {
        // Designações/nomes de campos de generalidades em linhas recolhidas não são validadas pela
        // regra inline (o input está desmontado). Validar aqui garante que não grava acima do limite.
        const generalities = (form.getValues('generalities') ?? []) as Array<{ designation?: string | null; fields?: Array<{ designation?: string | null }> }>;
        const hasTooLong = generalities.some(g =>
            (g?.designation?.length ?? 0) > GENERALITY_MAX_TEXT_LENGTH ||
            (g?.fields ?? []).some(fld => (fld?.designation?.length ?? 0) > GENERALITY_MAX_TEXT_LENGTH)
        );
        if (hasTooLong) {
            onSelectTab('generalities');
            Toast.warning(t('common.errors.max_length_detail', { maxLength: GENERALITY_MAX_TEXT_LENGTH }));
            return;
        }
        await saveBudget(model);
    }

    const onInvalid = () => {
        // Encaminha para a tab onde está o erro para o utilizador ver a mensagem inline (ex.: uma
        // designação/nome de campo de generalidade acima do limite de caracteres).
        const formErrors = form.formState.errors as Record<string, unknown>;
        if (formErrors.generalities) {
            onSelectTab('generalities');
        } else {
            onSelectTab('details');
        }
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (item: boolean) => {
        if (!item) {
            setShowConfirmDeleteModal(false)
            return;
        }
        try {
            Loading.show();

            await BudgetsService.remove(form.getValues())
            // Após apagar, o orçamento já não existe — voltar sempre para a listagem
            // (não para os detalhes, como faz o botão de voltar do cabeçalho).
            navigate(url);

            Loading.hide();
            Toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget', error);
            Toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false)
    }

    const onCloseConfirmPrintModal = (res: boolean) => {
        if (!res) {
            setPrintBudgetWithTechnicalFile(null)
        } else {
            void form.handleSubmit(onSubmit)().then(() => {
                void printBudget(printBudgetWithTechnicalFile!)
            });

            setPrintBudgetWithTechnicalFile(null)
        }
    }

    const getWorks = async (externalPartId: string | undefined | null) => {
        const currentWorkId = form.getValues('workId');

        try {
            Loading.show();

            const works = externalPartId
                ? await WorksService.getCatalogByExternalPartId(externalPartId)
                : await WorksService.getCatalog();
            setWorks(works);

            // Keep the selected work if it still belongs to the chosen external part;
            // only clear it (and its contacts) when it's no longer a valid option.
            if (!currentWorkId || !works.some(x => x.value === currentWorkId)) {
                form.setValue('workId', '');
                form.setValue('workName', '');
                form.setValue('workSubTitle', '');
                form.setValue('managerContactId', '');
                form.setValue('onSiteContactId', '');
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get works', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const getWorkContacts = async (workId: string | undefined | null) => {
        if (!workId) {
            setWorkContacts([]);
            return;
        }

        try {
            Loading.show();

            const workContacts = await WorksService.getWorkExternalPartContactsCatalog(workId);
            setWorkContacts(workContacts)

            if (workContacts.length === 1) {
                if (!form.getValues('managerContactId')) {
                    form.setValue('managerContactId', workContacts[0].value);
                }
                if (!form.getValues('onSiteContactId')) {
                    form.setValue('onSiteContactId', workContacts[0].value);
                }
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get works contacts', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    // Manual TS flow: the user picks (or changes) the origin budget in the dropdown. Mirror the
    // URL flow by pulling the same inheritable fields, but here we must also (re)load the
    // dependent catalogs ourselves so the inherited work/contacts resolve to visible options.
    const onChangeOriginBudget = async (originBudgetId: string | null | undefined) => {
        if (!originBudgetId) {
            await generateReference();
            return;
        }
        try {
            Loading.show();
            const origin = await BudgetsService.getById(originBudgetId, true);
            const inherited = getInheritableBudgetFields(origin);

            // Works are filtered by the (inherited) third party; ensure the inherited work is listed.
            const worksCatalog = inherited.externalPartId
                ? await WorksService.getCatalogByExternalPartId(inherited.externalPartId)
                : await WorksService.getCatalog();
            if (inherited.workId && inherited.workName && !worksCatalog.find(x => x.value === inherited.workId)) {
                worksCatalog.push({ label: inherited.workName, value: inherited.workId, subTitle: inherited.workSubTitle ?? '-' });
            }
            setWorks(worksCatalog);

            // Make sure the inherited family is selectable even if it's no longer in the active catalog.
            if (inherited.workTypeId && inherited.workTypeName) {
                setWorkTypes(prev => prev.find(x => x.value === inherited.workTypeId)
                    ? prev
                    : [...prev, { label: inherited.workTypeName!, value: inherited.workTypeId! }]);
            }

            // Contacts depend on the work; load them so the inherited contacts show up.
            setWorkContacts(inherited.workId
                ? await WorksService.getWorkExternalPartContactsCatalog(inherited.workId)
                : []);

            (Object.keys(inherited) as (keyof BudgetDto)[]).forEach(key => {
                form.setValue(key as any, inherited[key] as never);
            });

            // saveBudget spreads the Redux budget over the form values, so keep the inherited
            // numeric/default fields in sync there too (same reason as the language onChange).
            dispatch(updateBudget({
                languageId: inherited.languageId ?? null,
                defaultTaxId: inherited.defaultTaxId ?? null,
                defaultTaxValue: inherited.defaultTaxValue ?? null,
                defaultDiscountPercentage: inherited.defaultDiscountPercentage ?? null,
                defaultMarginPercentage: inherited.defaultMarginPercentage ?? null,
                defaultCommissionMarginPercentage: inherited.defaultCommissionMarginPercentage ?? null,
                defaultDecennialInsurancePercentage: inherited.defaultDecennialInsurancePercentage ?? null,
                decimalPlaces: inherited.decimalPlaces ?? DEFAULT_DECIMAL_PLACES,
            }));
            dispatch(calculateAllLinesTotals());

            // Clear validation errors on the now-filled required fields.
            form.clearErrors(['workId', 'workTypeId', 'languageId']);

            await generateReference();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t inherit data from origin budget', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const onGoBack = () => {
        // A partir de uma versão histórica ou do modo de edição, voltar para os detalhes do
        // próprio orçamento (o utilizador chegou aqui a partir dos detalhes), e não para a
        // listagem. Em detalhes/criação volta-se para a listagem.
        if ((type === 'version' || type === 'edit') && id) {
            navigateTo('details', id);
        } else {
            navigate(url);
        }
    }

    const navigateTo = (type: string, id?: string) => {
        navigate(url + '/' + type + (id ? ('/' + id) : ''));
    }

    const onSelectTab = async (k: string | null) => {
        if (!isDetails && (k === 'work' || k === 'lines' || k === 'generalities' || k === 'prices' || k === 'summary')) {
            // Valida o formulário inteiro para que TODOS os campos obrigatórios em falta
            // fiquem assinalados a vermelho (e limpa os que já estão preenchidos).
            const isValid = await form.trigger();
            if (!isValid) {
                onInvalid();
                return;
            }
        }

        setCurrentTabKey(k as TabKey)
    }

    const onCloseInvalidReferenceModal = (res: boolean) => {
        setShowInvalidReferenceModal(false);

        if (res) {
            void form.handleSubmit(onSubmit)();
        }
    }

    const onConfirmSaveRevision = async (res: boolean) => {
        setShowSaveRevisionModal(false);
        if (!res || !id) return;
        try {
            Loading.show();
            await BudgetsService.generateRevision(id);
            await getData();
            Toast.success(t('messages.record_save_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t generate budget revision', error);
            Toast.error(t('messages.record_save_error'));
        } finally {
            Loading.hide();
        }
    }

    const onUpdateAllPricesConfirm = async (mode: 'cancel' | 'prices_only' | 'prices_and_parameters') => {
        setShowUpdatePricesModal(false);
        if (mode === 'cancel') return;

        // Save current state + generate a history version BEFORE updating prices,
        // so the pre-update prices can always be recovered.
        let savedOk = false;
        await form.handleSubmit(
            async (model) => { savedOk = await saveBudget(model, true, 'PRICE_UPDATE'); },
            onInvalid
        )();
        if (!savedOk) return;

        try {
            Loading.show();
            const lines: BudgetLineDto[] = store.getState().budget.lines;
            const elementLines = lines.filter(l => l.elementId && (l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT));

            // Fetch each distinct element's parameters only once (the same element can appear on
            // many lines), in small parallel batches so we don't exhaust the DB connection pool.
            const uniqueElementIds = Array.from(new Set(elementLines.map(l => l.elementId!)));
            const parametersByElementId = new Map<string, BudgetLineParameterByElementDto>();
            const FETCH_BATCH_SIZE = 5;
            for (let i = 0; i < uniqueElementIds.length; i += FETCH_BATCH_SIZE) {
                const batch = uniqueElementIds.slice(i, i + FETCH_BATCH_SIZE);
                const fetched = await Promise.all(
                    batch.map(elementId => BudgetsService.getElementParameters(elementId, false))
                );
                batch.forEach((elementId, j) => parametersByElementId.set(elementId, fetched[j]));
            }

            for (const line of elementLines) {
                const parametersData = parametersByElementId.get(line.elementId!)!;
                const hasNewPrice = !!parametersData.price;
                // Ao repor o preço-base do elemento, limpar a expressão de preço; caso contrário o
                // recálculo (updateLinesExpressionValues) voltaria a sobrepor o valor com a fórmula antiga.
                const newGrossUnitPrice = hasNewPrice ? parametersData.price : line.grossUnitPrice;
                const newGrossUnitPriceExpression = hasNewPrice ? null : line.grossUnitPriceExpression;

                if (mode === 'prices_only') {
                    dispatch(updateLine({
                        key: line.key,
                        grossUnitPrice: newGrossUnitPrice,
                        grossUnitPriceExpression: newGrossUnitPriceExpression,
                    }));
                } else {
                    const newParams = parametersData.parameters ?? [];
                    const convertedParams = newParams.map(p => convertParameterExpressions(p, newParams));
                    const merged = mergeWithExistingParameters(convertedParams, line.parameters);
                    dispatch(updateLine({
                        key: line.key,
                        parameters: merged,
                        grossUnitPrice: newGrossUnitPrice,
                        grossUnitPriceExpression: newGrossUnitPriceExpression,
                        changedParameters: true
                    }));
                }
            }

            dispatch(calculateAllLinesTotals());
            Toast.success(t('budgets.budget.update_all_prices.success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t update all prices', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    };

    const onShowistory = async () => {
        if (id) {
            const histories = await BudgetsService.getHistoriesList(id);
            setBudgetHistories(histories);
            setShowHistoryModal(true);
        }
    }

    const renderHistoryItem = (item: BudgetHistoriesDto, index: number) => {
        // A transição de estado é GRAVADA no motivo no momento do "Guardar Revisão" como
        // "STATUS_CHANGE:<de>:<para>", por isso fica presa à revisão onde o estado mudou e mostra
        // sempre os estados corretos. (Fallback para formato antigo só com "<para>".)
        const statusParts = item.reason?.startsWith(STATUS_CHANGE_PREFIX)
            ? item.reason.substring(STATUS_CHANGE_PREFIX.length).split(':')
            : [];
        const fromStatus = statusParts.length >= 2 ? statusParts[0] : item.status;
        const toStatus = statusParts.length >= 2 ? statusParts[1] : statusParts[0];

        // "Copiado do orçamento ..." — a origem vem gravada como "número:revisão:versão". A versão e a
        // revisão são os 2 últimos segmentos; o resto é o número (que pode conter ':').
        let copiedFrom: { reference: string; revision: string; version: string } | null = null;
        if (item.reason?.startsWith(COPIED_FROM_PREFIX)) {
            const parts = item.reason.substring(COPIED_FROM_PREFIX.length).split(':');
            const displayVersion = parts.pop();
            const version = parts.pop();
            copiedFrom = {
                reference: parts.join(':'),
                revision: versionToLetter(Number(version)),
                version: formatBudgetVersion(Number(displayVersion)),
            };
        }
        return (
            <>
                <div>
                    <div
                        className={styles.historyVersion}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={item.number ?? ''}
                    >
                        {versionToLetter(item.version)}
                    </div>
                    {item.displayVersion > 0 && (
                        <span
                            className={styles.historyDisplayVersion}
                            data-tooltip-id={BODY_TOOLTIP}
                            data-tooltip-content={t('budgets.budget.history_version_tooltip', {
                                version: formatBudgetVersion(item.displayVersion),
                                revision: versionToLetter(item.version)
                            })}
                        >
                            {`v${formatBudgetVersion(item.displayVersion)}`}
                        </span>
                    )}
                    <button
                        className={styles.button}
                        type="button"
                        onClick={() => window.open('/business/budgets/version/' + id + '/' + item.id, '_blank')}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.open_version')}
                    >
                        <FaEye />
                    </button>
                    {canStartRevision && (
                        <button
                            className={styles.button}
                            type="button"
                            onClick={() => { setShowHistoryModal(false); setRestoreTarget(item); }}
                            data-tooltip-id={BODY_TOOLTIP}
                            data-tooltip-content={t('budgets.budget.start_revision_from_version_tooltip')}
                        >
                            <FaPencilAlt />
                        </button>
                    )}
                    <ButtonDropdown
                        variant='empty'
                        size='small'
                        className={styles.button}
                        options={[
                            { label: t('budgets.budget.print_without_technical_file'), onClick: () => printBudgetVersion(item, false) },
                            { label: t('budgets.budget.print_with_technical_file'), onClick: () => printBudgetVersion(item, true) },
                        ]}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.print_version_tooltip')}
                    >
                        <FaUpload />
                    </ButtonDropdown>
                </div>
                <Text className={styles.dateTime}>
                    <DateFormat value={item.createdDate} format={DATE_TIME_FORMAT_DEFAULT} /> {' | ' + item.createdUser}
                </Text>
                {/* Prioridade da descrição: cópia de versão > mudança de estado > motivo simples
                    (atualização de preço / incremento de revisão). */}
                {copiedFrom
                    ? (
                        <Text className={styles.transition}>
                            {t('budgets.budget.history_copied_from', {
                                reference: copiedFrom.reference,
                                revision: copiedFrom.revision,
                                version: copiedFrom.version
                            })}
                        </Text>
                    )
                    : toStatus
                    ? (
                        <Text className={styles.transition}>
                            {t('budgets.budget.history_status_transition', {
                                from: t('budgets.status.' + fromStatus),
                                to: t('budgets.status.' + toStatus)
                            })}
                        </Text>
                    )
                    : item.reason === 'PRICE_UPDATE'
                        ? (
                            <Text className={styles.transition}>
                                {t('budgets.budget.history_price_update')}
                            </Text>
                        )
                        : item.reason === 'VERSION_INCREMENT'
                            ? (
                                <Text className={styles.transition}>
                                    {t('budgets.budget.history_version_increment')}
                                </Text>
                            )
                            : item.reason === 'GENERATE_VERSION'
                                ? (
                                    <Text className={styles.transition}>
                                        {t('budgets.budget.history_generate_version')}
                                    </Text>
                                )
                                : item.status && (
                                    <Text className={styles.transition}>
                                        {t('budgets.budget.history_status_set', {
                                            to: t('budgets.status.' + item.status)
                                        })}
                                    </Text>
                                )
                }
            </>
        );
    };

    useEffect(() => {
        void getData();
        // Restore a clipboard copied in another budget / before a refresh so it can
        // be pasted here.
        dispatch(hydrateClipboardFromStorage());
    }, [id, type]);

    useEffect(() => {
        BudgetsCache.save(PAGE_ID, budgetData);
    }, [budgetData]);

    const printBudget = async (includeTechnicalFiles: boolean) => {
        try {
            Loading.show();

            const model: BudgetToPdfDto = {
                budgetId: id as string,
                budgetFileModelId: form.getValues('budgetFileModelId') as string,
                includeTechnicalFiles
            }

            const name = form.getValues('number').replace(/[^a-z0-9]/gi, '_').toUpperCase();
            const url = BudgetsService.getPrintBudgetUrl(model,'Devis_' + name + '_V' + form.getValues('budgetHistoryVersion'));

            window.open(url, '_blank');
            form.setValue('isPrinted', true);
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t print budget', error);
            Toast.error(t('messages.record_save_error'));
        }
        Loading.hide();
    }

    const printBudgetVersion = (item: BudgetHistoriesDto, includeTechnicalFiles: boolean) => {
        try {
            Loading.show();

            const model: BudgetToPdfDto = {
                budgetId: id as string,
                budgetFileModelId: form.getValues('budgetFileModelId') as string,
                includeTechnicalFiles,
                historyId: item.id,
            }

            const name = (item.number ?? form.getValues('number')).replace(/[^a-z0-9]/gi, '_').toUpperCase();
            const url = BudgetsService.getPrintBudgetUrl(model, 'Devis_' + name + '_v' + formatBudgetVersion(item.displayVersion));

            window.open(url, '_blank');
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t print budget version', error);
            Toast.error(t('messages.record_save_error'));
        }
        Loading.hide();
    }

    const doRestoreFromVersion = async (historyId: string, incrementDisplayVersion: boolean) => {
        try {
            Loading.show();
            await BudgetsService.restoreFromVersion(id as string, historyId, incrementDisplayVersion);
            Toast.success(t('messages.record_save_success'));
            if (type === 'edit') {
                await getData();
            } else {
                navigateTo('edit', id);
            }
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t restore budget from version', error);
            Toast.error(t('messages.record_save_error'));
        }
        Loading.hide();
    }

    const restoreFromVersion = async () => {
        if (restoreTarget === null || !id) return;
        const historyId = restoreTarget.id;
        setRestoreTarget(null);

        // Edit mode may have unsaved changes: persist them with a normal save first (which bumps the
        // display version to the value archived in the history) so the backend snapshots the *current*
        // edited state before copying the chosen version over it. The backend then bumps the version
        // again for the copied content.
        if (type === 'edit') {
            await form.handleSubmit(
                async (model) => {
                    const ok = await saveBudget(model);
                    if (ok) {
                        await doRestoreFromVersion(historyId, true);
                    }
                },
                onInvalid
            )();
            return;
        }

        // Details/version view: nothing to save and no version bump — just archive the current state
        // at its version and copy the chosen content over it.
        await doRestoreFromVersion(historyId, false);
    }

    const printButtonOptions: ButtonDropdownOption[] = [
        { label: t('budgets.budget.print_without_technical_file'), onClick: () => isDetails ? printBudget(false) : setPrintBudgetWithTechnicalFile(false) },
        { label: t('budgets.budget.print_with_technical_file'), onClick: () => isDetails ? printBudget(true) : setPrintBudgetWithTechnicalFile(true) },
    ];

    const onGenerateVersion = async () => {
        if (!id) return;
        try {
            Loading.show();
            await BudgetsService.generateVersion(id);
            await getData();
            Toast.success(t('messages.record_save_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t generate budget version', error);
            Toast.error(t('messages.record_save_error'));
        } finally {
            Loading.hide();
        }
    };

    const plusButtonOptions: ButtonDropdownOption[] = [
        ...(form.watch('type') !== BudgetType.TS
            ? [{ label: t('budgets.budget.create_ts'), onClick: () => navigate(`${url}/create?originBudgetId=${id}`) }]
            : []),
        { label: t('common.duplicate'), onClick: () => navigate(`${url}/create?duplicateFromId=${id}`) },
        { label: t('budgets.budget.generate_version'), onClick: onGenerateVersion },
        { label: t('budgets.budget.generate_revision'), onClick: () => setShowSaveRevisionModal(true) },
        { label: t('budgets.budget.export_materials.menu_option'), onClick: () => setShowExportMaterialsModal(true) },
    ];

    if (!canRead) {
        return null;
    }

    const entityName = form.watch('number');
    const displayVersion = form.watch('displayVersion');
    const titlePrefix = type === 'create'
        ? t('common.new_title.budget')
        : type === 'edit'
            ? t('common.edit_title.budget')
            : t('common.details_title.budget');
    const entityLabel = type !== 'create' && entityName
        ? (displayVersion != null ? `${entityName} · v${formatBudgetVersion(displayVersion)}` : entityName)
        : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    return (
        <ScreenTitle title={title}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('budgets.list.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <PageHeader title={titlePrefix} subTitle={entityLabel} informationText={t('common.go_back')} showGoBack addSidebarSpacing={currentTabKey === 'lines' || currentTabKey === 'summary'} onGoBack={onGoBack}>
                <Button variant='forth' fw className={styles.totalPriceButton}>
                    <span className={styles.totalPriceLabel}>{t('budgets.budget.lines.totals.total_without_tax')}</span>
                    <span className={styles.totalPrice}>
                        <MoneyFormat value={budget.totalExcludingTax ?? 0} suffix={'€'} decimalScale={budget.decimalPlaces} />
                    </span>
                </Button>

                { type !== 'create' && (
                    <Button fw variant='secondary' onClick={onShowistory}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.history_tooltip')}>
                        <FaHistory />
                    </Button>
                )}

                {canWrite && (isDetails || type === 'edit') &&
                    <ButtonDropdown fw variant='secondary' options={printButtonOptions} disabled={!form.watch('budgetFileModelId')}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={!form.watch('budgetFileModelId') ? t('budgets.budget.select_file_model_to_print') : t('budgets.budget.print_tooltip')}>
                        <FaUpload />
                    </ButtonDropdown>}
                {canWrite && type === 'details' && (
                    <ButtonDropdown fw variant='secondary' options={plusButtonOptions}
                        data-tooltip-id={BODY_TOOLTIP}
                        data-tooltip-content={t('budgets.budget.add_options_tooltip')}>
                        <FaPlus />
                    </ButtonDropdown>
                )}
                {canWrite && !isDetails && <Button form='formBudget' type='submit'>{t('common.save')}</Button>}
                {canWrite && isDetails && <Button fw href={`${url}/edit/${id}`}>{t('common.edit')}</Button>}
                {canDelete && isDetails && (
                    <Button variant='danger' onClick={() => form.getValues('status') === BudgetStatus.ACCEPTED ? setShowCannotDeleteWonModal(true) : setShowConfirmDeleteModal(true)}>
                        <FaTrashAlt />
                    </Button>
                )}
            </PageHeader>
            <PageContainer>
                {type === 'version' && <div className={styles.versionWarning}>
                    <InfoMessage message={t('budgets.budget.version_warning')} type='information'/>
                </div>}
                <FormProvider {...form}>
                    <Form id='formBudget' handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate className={styles.form}>
                        {loaded && <>
                            {currentTabKey === 'lines' && !isDetails && (
                                <div className={styles.linesTopButtons}>
                                    <button type='button' className={styles.importButton} onClick={() => setShowUpdatePricesModal(true)}
                                        data-tooltip-id={BODY_TOOLTIP}
                                        data-tooltip-content={t('budgets.budget.update_all_prices.tooltip')}>
                                        <FaSyncAlt />
                                    </button>
                                    <button type='button' className={styles.importButton} onClick={() => setShowImportModal(true)}>
                                        <FaFileExcel /> {t('budgets.budget.import_structure.button')}
                                    </button>
                                </div>
                            )}
                            <Tabs activeKey={currentTabKey} onSelect={(k) => onSelectTab(k)}>
                                <Tab eventKey={'details'} title={t('budgets.budget.tab_details')}>
                                    <BudgetDetails
                                        type={type}
                                        externalParts={externalParts}
                                        works={works}
                                        workContacts={workContacts}
                                        workTypes={workTypes}
                                        budgetFileModels={budgetFileModels}
                                        languages={languages}
                                        languageIsoById={languageIsoById}
                                        generalityDefaults={generalityDefaults}
                                        taxes={taxes}
                                        onChangeExternalPart={getWorks}
                                        getWorkContacts={getWorkContacts}
                                        onChangeWorkType={generateReference}
                                        onChangeOriginBudget={onChangeOriginBudget}
                                        originBudgets={originBudgets}
                                    />
                                </Tab>
                                <Tab eventKey={'work'} title={t('budgets.budget.tab_work')}>
                                    {currentTabKey === 'work' && <BudgetWorkTab />}
                                </Tab>
                                <Tab eventKey={'lines'} title={t('budgets.budget.tab_lines')}>
                                    <BudgetLines
                                        type={type}
                                        taxes={taxes}
                                        units={units}
                                        suppliers={suppliers}
                                        elementTypes={elementTypes}
                                        budgetData={budgetData}
                                    />
                                </Tab>
                                <Tab eventKey={'generalities'} title={t('budgets.budget.tab_generalities')}>
                                    {currentTabKey === 'generalities' && (
                                        <BudgetGeneralities
                                            isDetails={isDetails}
                                            taxes={taxes}
                                            units={units}
                                            generalityDefaults={generalityDefaults}
                                            materializeDefault={materializeGeneralityDefault}
                                        />
                                    )}
                                </Tab>
                                <Tab eventKey={'prices'} title={t('budgets.budget.tab_prices')}>
                                    <BudgetPrices
                                        isDetails={isDetails}
                                        taxes={taxes}
                                        units={units}
                                    />
                                </Tab>
                                <Tab eventKey={'summary'} title={t('budgets.budget.tab_summary')}>
                                    {currentTabKey === 'summary' && (
                                        <BudgetSummary
                                            hiddenColumns={summaryHiddenColumns}
                                            columnsOrder={summaryColumnsOrder}
                                            isDetails={isDetails}
                                            onNavigateToLine={(lineKey) => {
                                                const lines: BudgetLineDto[] = store.getState().budget.lines;
                                                let current = lines.find((l: BudgetLineDto) => l.key === lineKey);
                                                while (current?.parentKey) {
                                                    dispatch(updateLine({ key: current.parentKey, isOpen: true }));
                                                    current = lines.find((l: BudgetLineDto) => l.key === current!.parentKey);
                                                }
                                                dispatch(setSelectedLineKey(lineKey));
                                                setCurrentTabKey('lines');
                                            }}
                                        />
                                    )}
                                </Tab>
                            </Tabs>
                        </>}
                    </Form>
                    <ImportStructureModal
                        isOpen={showImportModal}
                        onClose={() => setShowImportModal(false)}
                        units={units}
                        externalPartId={form.watch('externalPartId')}
                    />
                    <ExportMaterialsListModal
                        isOpen={showExportMaterialsModal}
                        onClose={() => setShowExportMaterialsModal(false)}
                        budgetId={id}
                        budgetNumber={form.watch('number')}
                        budgetName={form.watch('description')}
                        lines={budgetLines}
                        configs={form.watch('configs') ?? []}
                    />
                </FormProvider>
            </PageContainer>
            {currentTabKey === 'lines' && (
                <ColumnsOptionsSidebar
                    columns={linesTableColumns}
                    hiddenColumns={budgetData.hiddenColumns}
                    onChangeHiddenColumns={(hc) => setBudgetData(d => ({ ...d, hiddenColumns: hc }))}
                    onChangeColumnsOrder={(oc) => setBudgetData(d => ({ ...d, columnsOrder: oc }))}
                />
            )}
            {currentTabKey === 'summary' && (
                <ColumnsOptionsSidebar
                    columns={summaryTableColumns}
                    hiddenColumns={summaryHiddenColumns}
                    onChangeHiddenColumns={onChangeSummaryHiddenColumns}
                    onChangeColumnsOrder={onChangeSummaryColumnsOrder}
                    sections={[
                        {
                            title: t('budgets.budget.summary.visible_fields'),
                            columns: summaryConfigColumns,
                            hiddenColumns: summaryConfigHidden,
                            onChangeHiddenColumns: onChangeSummaryConfigHidden,
                            onChangeColumnsOrder: onChangeSummaryConfigOrder,
                        },
                        {
                            title: t('common.visible_columns'),
                            columns: summaryTableColumns,
                            hiddenColumns: summaryHiddenColumns,
                            onChangeHiddenColumns: onChangeSummaryHiddenColumns,
                            onChangeColumnsOrder: onChangeSummaryColumnsOrder,
                        },
                    ]}
                />
            )}
            <ConfirmDeleteModal
                itemName={form.getValues('description')}
                isOpen={showConfirmDeleteModal}
                onClose={onDelete}
            />

            <InformationModal
                title={t('budgets.budget.cannot_delete_won_title')}
                message={t('budgets.budget.cannot_delete_won_message')}
                isOpen={showCannotDeleteWonModal}
                onClose={() => setShowCannotDeleteWonModal(false)}
            />

            <YesNoModal
                title={t('budgets.budget.save_before_print_modal_title')}
                message={t('budgets.budget.save_before_print_modal_message')}
                isOpen={printBudgetWithTechnicalFile != null}
                onClose={onCloseConfirmPrintModal}
            />

            <YesNoModal
                title={t('budgets.budget.save_revision_title')}
                message={t('budgets.budget.save_revision_message')}
                isOpen={showSaveRevisionModal}
                onClose={onConfirmSaveRevision}
            />

            <YesNoModal
                title={t('budgets.budget.invalid_reference_title')}
                message={t('budgets.budget.invalid_reference_message', { newReference: form.getValues('number') })}
                isOpen={showInvalidReferenceModal}
                onClose={onCloseInvalidReferenceModal}
            />

            {showUpdatePricesModal && (
                <Modal isOpen={showUpdatePricesModal} style={{ content: { minWidth: 400, maxWidth: 700 } }} onRequestClose={() => onUpdateAllPricesConfirm('cancel')}>
                    <div>
                        <ModalHeader>
                            <Header>{t('budgets.budget.update_all_prices.modal_title').toUpperCase()}</Header>
                        </ModalHeader>
                        <ModalBody>
                            <Text style={{ whiteSpace: 'pre-line', marginBottom: '1.5rem' }}>{t('budgets.budget.update_all_prices.modal_message')}</Text>
                        </ModalBody>
                        <ModalFooter className="text-right">
                            <Button fw variant='secondary' onClick={() => onUpdateAllPricesConfirm('cancel')}>{t('common.cancel')}</Button>
                            <Button fw onClick={() => onUpdateAllPricesConfirm('prices_only')}>{t('budgets.budget.update_all_prices.prices_only')}</Button>
                            <Button fw onClick={() => onUpdateAllPricesConfirm('prices_and_parameters')}>{t('budgets.budget.update_all_prices.prices_and_parameters')}</Button>
                        </ModalFooter>
                    </div>
                </Modal>
            )}

            <HistoryModal
                title={t('budgets.budget.history_model_title')}
                isOpen={showHistoryModal}
                items={budgetHistories}
                renderItem={renderHistoryItem}
                onClose={() => setShowHistoryModal(false)}
            />

            <Modal isOpen={restoreTarget !== null} style={{ content: { minWidth: 440 } }} onRequestClose={() => setRestoreTarget(null)}>
                <div>
                    <ModalHeader>
                        <Header>{t('budgets.budget.restore_from_version_title').toUpperCase()}</Header>
                    </ModalHeader>
                    <ModalBody>
                        <Text dangerouslySetInnerHTML={{ __html: t('budgets.budget.restore_from_version_message', { reference: restoreTarget?.number ?? '', version: (restoreTarget?.displayVersion ?? 0) > 0 ? ' v' + formatBudgetVersion(restoreTarget?.displayVersion) : '' }) }}></Text>
                    </ModalBody>
                    <ModalFooter className="text-right">
                        <Button fw variant="secondary" onClick={() => setRestoreTarget(null)}>{t('common.cancel')}</Button>
                        <Button onClick={() => restoreFromVersion()}>{t('budgets.budget.restore_replace')}</Button>
                    </ModalFooter>
                </div>
            </Modal>

        </ScreenTitle>
    );
}

export default BudgetScreen;
